/**
 * NavShell · RISO_FINAL_DESIGN nav wrapper + Atelier overlay state owner.
 * Per slot_riso_final_ds Phase A1.5 (15 MAY 2026) · ROLE lines 97-102.
 *
 * Viewport-aware composition: renders Sidebar (desktop/tablet) or BottomTabs
 * (mobile) based on viewport width. Owns AtelierOverlay open/close state
 * and intercepts atelier nav clicks to toggle overlay instead of routing.
 *
 * Phase D extension (16 MAY 2026 · slot_riso_new_screens · Q-D4 b):
 *   URL hash listener. When window.location.hash matches #/atelier?...,
 *   NavShell opens the overlay and passes the route query (or capstone
 *   shortcut) to AtelierOverlay initialRoute prop.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { BottomTabs } from './BottomTabs';
import { AtelierOverlay } from './AtelierOverlay';
import { SiteFooter } from './legal/SiteFooter';
import { useGlossary } from '@/contexts/GlossaryContext';

export type NavShellDevice = 'desktop' | 'tablet' | 'mobile';

// slot_homework_rework Ф1b (17 JUN 2026 · Genie): kept at 1100 (the 768 experiment
// was reverted). Tablets stay on bottom-tabs — homework lives in the «Доска» tab's
// pop-up menu (Тикеты · Домашка), not a side-rail accordion.
const MOBILE_BREAKPOINT_PX = 1100;
const TABLET_BREAKPOINT_PX = 1366;

export interface NavShellProps {
  screen: string;
  onNav: (key: string) => void;
  children?: ReactNode;
  deviceOverride?: NavShellDevice;
}

function detectDevice(): NavShellDevice {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w <= MOBILE_BREAKPOINT_PX) return 'mobile';
  if (w <= TABLET_BREAKPOINT_PX) return 'tablet';
  return 'desktop';
}

/**
 * Parse window.location.hash to initial Atelier route hint.
 * Returns null when hash is NOT an atelier deep-link.
 */
/** VIS-5: флаг «оверлей Ательера открыт» — пережёвывает полную перезагрузку без URL-hash. */
const ATELIER_OPEN_FLAG = 'qastudio.v1.atelier.overlayOpen';

export function parseAtelierHash(hash: string): string | null {
  const m = hash.match(/^#\/atelier(?:\?(.*))?$/);
  if (!m) return null;
  const query = m[1] ?? '';
  if (!query) return 'root';
  const params = new URLSearchParams(query);
  const capstone = params.get('capstone');
  if (capstone) return `capstone-${capstone}`;
  const route = params.get('route');
  if (route) return route;
  return 'root';
}

export function NavShell({ screen, onNav, children, deviceOverride }: NavShellProps) {
  const [device, setDevice] = useState<NavShellDevice>(() => deviceOverride ?? detectDevice());
  const [atelierOpen, setAtelierOpen] = useState(false);
  const [atelierRoute, setAtelierRoute] = useState<string | null>(null);

  // slot_vocabulary_navbar (17 MAY 2026): glossary opener pulled from
  // GlossaryContext (provided by App.tsx Router). Falls back к noop when no
  // provider is mounted (component-level tests).
  const { open: openGlossary } = useGlossary();

  // B.0 · CF-A1-focus-management absorption (16 MAY 2026).
  const atelierButtonRef = useRef<HTMLButtonElement | null>(null);
  const prevAtelierOpen = useRef(false);
  // VIS-12: курсовой хеш до открытия Артели — чтобы вернуть его при закрытии.
  const prevCourseHashRef = useRef<string | null>(null);

  // Listen for viewport resize.
  useEffect(() => {
    if (deviceOverride) return;
    if (typeof window === 'undefined') return;
    const onResize = () => setDevice(detectDevice());
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [deviceOverride]);

  // Sync deviceOverride changes.
  useEffect(() => {
    if (deviceOverride) setDevice(deviceOverride);
  }, [deviceOverride]);

  // B.0 · return-focus to Atelier nav button on overlay close transition.
  useEffect(() => {
    if (prevAtelierOpen.current && !atelierOpen) {
      atelierButtonRef.current?.focus();
    }
    prevAtelierOpen.current = atelierOpen;
  }, [atelierOpen]);

  // Phase D · Q-D4 b · URL hash listener (deep-link to overlay open).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sync = () => {
      const route = parseAtelierHash(window.location.hash);
      if (route !== null) {
        setAtelierRoute(route);
        setAtelierOpen(true);
      }
    };
    sync();
    // VIS-5: восстановить оверлей после полной перезагрузки (флаг, без URL-hash).
    try {
      if (window.localStorage.getItem(ATELIER_OPEN_FLAG) === '1') {
        setAtelierOpen(true);
      }
    } catch {
      /* ignore */
    }
    window.addEventListener('hashchange', sync);
    return () => {
      window.removeEventListener('hashchange', sync);
    };
  }, []);

  // VIS-12: вернуть курсовой хеш при закрытии Артели (если сейчас #/atelier).
  const restoreCourseHash = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!window.location.hash.startsWith('#/atelier')) return;
    const prev = prevCourseHashRef.current;
    prevCourseHashRef.current = null;
    const target =
      prev && !prev.startsWith('#/atelier')
        ? `${window.location.pathname}${window.location.search}${prev}`
        : `${window.location.pathname}${window.location.search}`;
    try {
      window.history.replaceState(null, '', target);
    } catch {
      window.location.hash = prev && !prev.startsWith('#/atelier') ? prev : '';
    }
  }, []);

  const handleNav = useCallback(
    (key: string) => {
      if (key === 'atelier') {
        const next = !atelierOpen;
        setAtelierOpen(next);
        // VIS-12: оверлей-флаг в localStorage (переживает рефреш) + стабильный URL
        // «#/atelier», чтобы адрес не «прыгал» по курсовым маршрутам. Курсовой
        // роутер игнорирует #/atelier (parseCourseHash → null), экран не меняется.
        if (typeof window !== 'undefined') {
          try {
            if (next) {
              window.localStorage.setItem(ATELIER_OPEN_FLAG, '1');
              if (!window.location.hash.startsWith('#/atelier')) {
                prevCourseHashRef.current = window.location.hash;
                window.history.replaceState(null, '', '#/atelier');
              }
            } else {
              window.localStorage.removeItem(ATELIER_OPEN_FLAG);
              restoreCourseHash();
            }
          } catch {
            /* ignore */
          }
        }
        onNav(key);
        return;
      }
      // slot_vocabulary_navbar (17 MAY 2026): intercept 'glossary' foot-nav key,
      // open the App-owned GlossaryOverlay via context opener. Mirrors the
      // atelier intercept above. Still emits onNav so consumers (analytics, tests)
      // can observe the click — overlay open is a side-effect, not a route change.
      if (key === 'glossary') {
        openGlossary();
        onNav(key);
        return;
      }
      onNav(key);
    },
    [onNav, openGlossary, atelierOpen, restoreCourseHash],
  );

  const closeAtelier = useCallback(() => {
    setAtelierOpen(false);
    setAtelierRoute(null);
    try {
      window.localStorage.removeItem(ATELIER_OPEN_FLAG);
    } catch {
      /* ignore */
    }
    // VIS-12: вернуть курсовой хеш (или снять #/atelier), а не просто обнулить.
    restoreCourseHash();
  }, [restoreCourseHash]);

  // H.4-EXT3.4 (17 MAY 2026 slot_riso_phase_h_final_smoke): body class sync с atelierOpen.
  // CSS rule `body.atelier-overlay-open .side-rail { width: 220px; ... }` expands
  // sidebar к full label visibility per ADR-010. Without this sidebar stayed at 64px
  // showing labels clipped к 1 character («С С С Д» truncation pattern).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    // Класс ставим и на body, и на <html>: overflow:hidden только на body не убирал
    // скролл фона (браузер выносил его на documentElement → второй скроллбар, 9 JUL).
    const root = document.documentElement;
    if (atelierOpen) {
      document.body.classList.add('atelier-overlay-open');
      root.classList.add('atelier-overlay-open');
    } else {
      document.body.classList.remove('atelier-overlay-open');
      root.classList.remove('atelier-overlay-open');
    }
    return () => {
      document.body.classList.remove('atelier-overlay-open');
      root.classList.remove('atelier-overlay-open');
    };
  }, [atelierOpen]);

  return (
    <>
      {device === 'mobile' ? (
        <BottomTabs screen={screen} onNav={handleNav} />
      ) : (
        <Sidebar
          screen={screen}
          device={device}
          onNav={handleNav}
          atelierButtonRef={atelierButtonRef}
        />
      )}
      {/* Sticky-footer flow: the screen grows to fill the viewport so the global
          SiteFooter pins to the bottom instead of adding height below already-tall
          content → no phantom scrollbar on screens that otherwise fit (fix 2 JUL). */}
      <div className="app-shell-scroll">
        {children}
        <SiteFooter screen={screen} />
      </div>
      <AtelierOverlay
        open={atelierOpen}
        onClose={closeAtelier}
        device={device}
        initialRoute={atelierRoute ?? undefined}
      />
    </>
  );
}

export const NAV_SHELL_BREAKPOINTS = {
  mobile: MOBILE_BREAKPOINT_PX,
  tablet: TABLET_BREAKPOINT_PX,
} as const;
